import type * as THREE from 'three';

// D309 (the user, playing it: "colors need to be more saturated" — scope: TILES and PROPS only).
//
// A per-material VIBRANCE lift, never a post-process pass: a full-screen grade is one more pass
// and render target every frame (the D186/D287/D294 work ran the other way), and it would grade
// the pieces too. Applied after lighting, before the output encode, at `opaque_fragment` (the
// cavity shading's own marker, occlusion.ts). Two properties are the point:
//
//   - it PRESERVES LUMA — mix(luma, c, t) keeps luma at any t — so exposure does not move, which
//     is the trap D145 rejected (a brighter, "correctly lit" board looked wrong);
//   - it lifts MUTED colours more than vivid ones (t grows with 1 − saturation), so the terrain
//     gains colour without reaching the seat colours. D108 made the terrain deliberately dull so
//     that chroma was the one axis the pieces own; pieces, ports and the sea are NOT graded, and
//     that split is structural (only tile and prop materials are ever passed in here).
//
// One uniform object is shared by every graded material, so a value changed at runtime (the
// `?vibrance=` override, for tuning by eye on the live board) reaches all of them at once.

/**
 * The chosen lift. 0 is the board as it was. MEASURED on the sea probe at play zoom (land pixels,
 * water excluded): 0.45 moved mean saturation only +8% (0.436 → 0.472), which read as no change;
 * 0.9 moved it +17% (→ 0.512) and chroma +18%, with the terrain's 90th-percentile chroma (0.52)
 * still well under the seat colours. 0.8 ships; `?vibrance=` tunes it live.
 */
export const BOARD_VIBRANCE = 0.8;

const vibranceUniform = { value: BOARD_VIBRANCE };

/** Tune the lift live (a debug/URL override); every graded material reads this one uniform. */
export function setBoardVibrance(v: number): void {
  if (Number.isFinite(v)) vibranceUniform.value = Math.min(2, Math.max(0, v));
}
export function boardVibrance(): number {
  return vibranceUniform.value;
}

/** Linear-light luma (Rec. 709), the GLSL's own weights. */
const luma = (c: readonly [number, number, number]): number => 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];

/** The GLSL below, in TS — what the spec can hold the shader to. */
export function vibrance(c: readonly [number, number, number], k: number): [number, number, number] {
  const mx = Math.max(c[0], c[1], c[2]);
  const mn = Math.min(c[0], c[1], c[2]);
  const sat = mx > 0 ? (mx - mn) / mx : 0;
  const t = 1 + k * (1 - sat);
  const l = luma(c);
  return [Math.max(0, l + (c[0] - l) * t), Math.max(0, l + (c[1] - l) * t), Math.max(0, l + (c[2] - l) * t)];
}

export const GRADE_GLSL = [
  '{',
  '  float gL = dot(gl_FragColor.rgb, vec3(0.2126, 0.7152, 0.0722));',
  '  float gMx = max(gl_FragColor.r, max(gl_FragColor.g, gl_FragColor.b));',
  '  float gMn = min(gl_FragColor.r, min(gl_FragColor.g, gl_FragColor.b));',
  '  float gSat = gMx > 0.0 ? (gMx - gMn) / gMx : 0.0;',
  '  gl_FragColor.rgb = max(mix(vec3(gL), gl_FragColor.rgb, 1.0 + uBoardVibrance * (1.0 - gSat)), 0.0);',
  '}',
].join('\n');

/** Graded materials. A WeakSet, not a userData flag: `clone()` copies userData but never the
 *  shader patch, so a flag would mark a clone graded that is not. */
const graded = new WeakSet<THREE.Material>();

/**
 * Grade this material, in place, once. It CHAINS whatever shader patch it already carries (a
 * prop's bend, the flock's skinning) — `clone()` never copies `onBeforeCompile`, so the grade is
 * applied to the final material where it is used, never to a shared base it would not survive.
 */
export function gradeMaterial<M extends THREE.Material>(mat: M): M {
  if (graded.has(mat)) return mat;
  graded.add(mat);
  const prev = mat.onBeforeCompile.bind(mat);
  const prevKey = mat.customProgramCacheKey.bind(mat);
  mat.onBeforeCompile = (shader, renderer): void => {
    prev(shader, renderer);
    shader.uniforms['uBoardVibrance'] = vibranceUniform;
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\nuniform float uBoardVibrance;')
      .replace('#include <opaque_fragment>', `#include <opaque_fragment>\n${GRADE_GLSL}`);
  };
  mat.customProgramCacheKey = (): string => `${prevKey()}|kolony-grade`;
  mat.needsUpdate = true;
  return mat;
}

/** True when `gradeMaterial` has patched this material. */
export function isGraded(mat: THREE.Material): boolean {
  return graded.has(mat);
}
